import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ConfirmationDialog } from 'components/v4';
import { useDisclosure } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaUserPlus,
    FaFileUpload,
    FaFileCsv,
    FaSync,
    FaTools,
} from 'react-icons/fa';
import { getNotification } from 'API/General/getNotification';
import useUsers from 'hooks/useUsers';
import walkthroughIds from 'utils/walkthroughIds';
import CollapsibleButtonGroup from 'components/presentational/CollapsibleButtonGroup';
import { getUsersButtonVisibility } from './usersButtonVisibility';

const UsersButtons = ({ selectedPlatform, forceOverflowing }) => {
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { ufBuilderBtn } = walkthroughIds;
    const { pathname } = useLocation();

    const isReAccountsPath = pathname.includes('/users');
    const { handleBuilderBtn } = useUsers();
    const navigate = useNavigate();
    const [syncState, setSyncState] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();

    const handleSyncConfirm = () => {
        onClose();
        setSyncState('loading');
        getNotification('company', currentCompany)
            .then(() => setSyncState('succeed'))
            .catch(() => setSyncState('error'));
    };
    const syncButton = () => {
        switch (syncState) {
            case 'succeed':
                return {
                    text: 'Syncing...',
                };
            case 'error':
                return {
                    text: 'Sync Failed',
                };
            case '':
                return {
                    text: 'Sync Users',
                };
            case 'loading':
                return {
                    text: 'Syncing...',
                };
            default:
                return { color: 'outline-primary', text: '' };
        }
    };
    const isVisible = getUsersButtonVisibility({
        selectedPlatform,
        isReAccountsPath,
    });

    const buttonItems = [
        isVisible.addUser && {
            key: 'add-user',
            label: 'Add Endpoint',
            icon: FaUserPlus,
            onClick: () => navigate('/users/create'),
        },
        {
            key: 'csv-update',
            label: 'CSV Update',
            icon: FaFileUpload,
            onClick: () => navigate('/users/csv-users'),
            walkthroughId: ufBuilderBtn,
        },
        isVisible.csvCreate && {
            key: 'csv-create',
            label: 'CSV Create',
            icon: FaFileCsv,
            onClick: () => navigate('/users/csv-create'),
        },
        {
            key: 'sync-users',
            label: syncButton().text,
            icon: FaSync,
            onClick: () => {
                if (syncState === '') onOpen();
            },
            walkthroughId: ufBuilderBtn,
        },
        isVisible.builder && {
            key: 'builder',
            label: 'Builder',
            icon: FaTools,
            tooltip: 'Automatically create Call Flows within Microsoft Teams',
            onClick: handleBuilderBtn,
            walkthroughId: ufBuilderBtn,
        },
    ];

    return (
        <>
            <CollapsibleButtonGroup
                items={buttonItems}
                menuLabel="Users Actions"
                forceOverflowing={forceOverflowing}
                pl={5}
            />

            <ConfirmationDialog
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleSyncConfirm}
                title="Sync Users"
                message={
                    <>
                        <p>
                            This action shall force a full User Sync with your
                            service. Depending on the number of users, this may
                            take some time. This is only required if you believe
                            users or numbers are missing in the current list.
                        </p>
                        <br />
                        <p>Please confirm you wish to run a full User Sync.</p>
                    </>
                }
                confirmText="OK"
            />
        </>
    );
};

export default UsersButtons;
